<template>
  <div>
    <b-sidebar
      ref="sb"
      v-model="state"
      v-resize="onResize"
      :width="`${width}px`"
      :no-header-close="true"
      right
      shadow
      no-close-on-esc
      no-close-on-backdrop
    >
      <div v-if="state" class="bg-white px-1 px-lg-3">
        <b-tabs
          v-model="activeTab"
          pills
          :card="false"
          nav-wrapper-class="py-2"
          content-class="px-0"
          @activate-tab="onShowTab"
        >
          <b-tab
            :title="`${$t('fCustomer_usrNot_receivedTitle')} (${info.notes.received})`"
            active
          >
            <data-layout
              v-if="userId && activeTab === 0"
              ref="dtReceived"
              :fields="fields"
              :filter="filterReceived"
              :order="order"
              :repo="repo"
              :parent-id="userId"
              :height="height"
              :limit="50"
              :set-item-variant="setItemVariant"
              card-collapse
              @on-sort="onSort"
            >
              <template #card(header)="{ data: { item }}">
                <user-profile-link v-if="item" :user="item.author" />
                <div v-if="item" class="small">
                  <span class="text-muted">{{ $dateFormat(item.created_at) }}</span>
                </div>
                <div
                  v-shtml="item.body"
                  style="overflow:hidden; text-overflow: ellipsis;"
                />
              </template>
              <template #cell(author_id)="{ data }">
                <user-profile-item
                  :user="data.item.author"
                />
              </template>
              <template #cell(market)="{ data: { item } }">
                <market-flag :market="item.author.market" />
              </template>
              <template #cell(is_private)="{ data }">
                <font-awesome-icon v-if="data.item.is_private" :icon="['fas', 'check']" class="text-success" />
                <font-awesome-icon v-else :icon="['fas', 'xmark']" class="text-danger" />
              </template>
              <template #cell(created_at)="{ data }">
                <span v-shtml="data.field.formatter(data.item.created_at, data.key, data.item)" />
              </template>
              <template #cell(body)="{ data: { item } }">
                <span
                  v-shtml="item.body"
                  class="d-block"
                  style="width: 260px;"
                />
              </template>
            </data-layout>
          </b-tab>
          <b-tab
            :title="`${$t('fCustomer_usrNot_sentTitle')} (${info.notes.left})`"
          >
            <data-layout
              v-if="userId && activeTab === 1"
              ref="dtSent"
              :fields="fields"
              :filter="filterSent"
              :order="order"
              :repo="repo"
              :parent-id="userId"
              :height="height"
              :limit="50"
              :set-item-variant="setItemVariant"
              card-collapse
              @on-sort="onSort"
            >
              <template #card(header)="{ data: { item }}">
                <user-profile-link v-if="item" :user="item.user" />
                <div v-if="item" class="small">
                  <span class="text-muted">{{ $dateFormat(item.created_at) }}</span>
                </div>
                <div
                  v-shtml="item.body"
                  style="overflow:hidden; text-overflow: ellipsis;"
                />
              </template>
              <template #cell(user_id)="{ data }">
                <user-profile-item
                  :user="data.item.user"
                />
              </template>
              <template #cell(market)="{ data: { item } }">
                <market-flag :market="item.user.market" />
              </template>
              <template #cell(is_private)="{ data }">
                <font-awesome-icon v-if="data.item.is_private" :icon="['fas', 'check']" class="text-success" />
                <font-awesome-icon v-else :icon="['fas', 'xmark']" class="text-danger" />
              </template>
              <template #cell(created_at)="{ data }">
                <span v-shtml="data.field.formatter(data.item.created_at, data.key, data.item)" />
              </template>
              <template #cell(body)="{ data: { item } }">
                <span
                  v-shtml="item.body"
                  class="d-block"
                  style="width: 260px;"
                />
              </template>
            </data-layout>
          </b-tab>
        </b-tabs>
      </div>
      <template #header>
        <b-link class="mr-2 text-info" @click="onCancel">
          <font-awesome-icon v-if="isMobile" :icon="['fas', 'arrow-left']" class="mr-2" />
          <span v-shtml="parentTitle" />
        </b-link>
        - {{ displayTitle }}
      </template>
      <template #footer>
        <div v-if="!isMobile" class="d-flex bg-light align-items-center px-3 py-2">
          <strong class="mr-auto" />
          <b-btn variant="secondary" size="sm" @click="onCancel">
            {{ $t('eDoc_form_a_close') }}
          </b-btn>
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { isServer } from '@/mixins/helpers'

export default {
  name: 'UserNotes',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    userId: {
      type: [String, Number],
      required: true
    },
    idx: {
      type: Number,
      default: -1
    },
    info: {
      type: Object,
      default: () => ({})
    },
    parentTitle: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      repo: 'customers.notes',
      filter: [],
      order: { by: 'updated_at', dir: 'desc' },
      state: this.value,
      activeTab: null,
      busy: false,
      height: 500,
      width: 300
    }
  },
  async fetch () {
    if (this.$refs.dtReceive && this.activeTab === 0) {
      await this.$refs.dtReceived.$fetch()
    } else if (this.$refs.dtSent && this.activeTab === 1) {
      await this.$refs.dtSent.$fetch()
    }
  },
  computed: {
    ...mapGetters({
      deviceInfo: 'device'
    }),
    isMobile () {
      return this.deviceInfo?.type === 'mobile' || false
    },
    displayTitle () {
      return `${this.$t('fCustomer_usrNot_title')} (${this.total})`
    },
    filterReceived () {
      return [
        { column: 'user_id', value: this.userId, permanent: true }
      ].concat(this.filter)
    },
    filterSent () {
      return [
        { column: 'author_id', value: this.userId, permanent: true }
      ].concat(this.filter)
    },
    total () {
      return this.info?.notes?.tot || 0
    },
    fields () {
      const fields = [
        {
          key: 'id',
          label: this.$t('fCustomer_usrNot_f_id'),
          sortable: true,
          class: 'col-1'
        },
        {
          key: 'created_at',
          label: this.$t('fCustomer_usrNot_f_date'),
          sortable: true,
          class: 'col-1',
          formatter: (value, key, item) => {
            return this.$dateFormat(value, 'DD/MM/YYYY<br>HH:mm')
          }
        }
      ]
      if (this.activeTab === 0) {
        fields.push({
          key: 'author_id',
          label: this.$t('fCustomer_usrNot_f_author'),
          sortable: true,
          class: 'col-4',
          cardFullWidth: true
        })
      } else if (this.activeTab === 1) {
        fields.push({
          key: 'user_id',
          label: this.$t('fCustomer_usrNot_f_user'),
          sortable: true,
          class: 'col-4',
          cardFullWidth: true
        })
      }
      fields.push({
        key: 'market',
        label: this.$t('fCustomer_usrNot_f_market'),
        class: 'col-1 text-center',
        thClass: 'text-center'
      })
      fields.push({
        key: 'body',
        label: this.$t('fCustomer_usrNot_f_text'),
        thClass: 'text-center',
        tdClass: 'text-wrap',
        sortable: true,
        cardFullWidth: true
      })
      return fields
    }
  },
  watch: {
    value (n) {
      this.state = n
      if (!n) {
        this.activeTab = null
        this.flushData()
      }
      this.$nextTick(() => {
        this.calcHeight()
        this.calcWidth()
      })
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.calcHeight()
      this.calcWidth()
    })
    if (window) {
      window.addEventListener('resize', this.calcHeight)
    }
  },
  beforeDestroy () {
    if (window) {
      window.removeEventListener('resize', this.calcHeight)
    }
  },
  methods: {
    ...mapActions({
      call: 'repos/call',
      flush: 'repos/flush',
      flushError: 'repos/flushError'
    }),
    onSort (order) {
      this.order = order
    },
    flushData () {
      this.flush({
        repo: this.repo,
        method: 'index',
        id: this.itemId,
        idx: this.idx
      })
    },
    onCancel () {
      this.$emit('input', false)
    },
    onShowTab (activeTab) {
      this.flushData()
      this.$forceUpdate()
      this.$nextTick(() => {
        this.$fetch()
      })
    },
    calcHeight () {
      if (!isServer && window) {
        const $sidebar = this.$refs.sb?.$el?.querySelector('.b-sidebar-body')
        const $pager = this.$refs.sb?.$el?.querySelector('.pager')
        const $tabsNav = this.$refs.sb?.$el?.querySelector('.b-sidebar-body .tabs .nav')?.parentElement
        const sidebarHeight = $sidebar?.clientHeight || 0
        const pagerHeight = $pager?.clientHeight || 0
        const tabsNavHeight = $tabsNav?.clientHeight || 0
        // calc.
        this.height = sidebarHeight - pagerHeight - tabsNavHeight
      }
    },
    calcWidth () {
      let width = 0
      if (!isServer) {
        if (this.deviceInfo?.type === 'mobile') {
          width = document.body.offsetWidth
        } else {
          width = Math.round(document.body.offsetWidth * 0.80) - (15 * (this.idx + 1))
        }
      }
      this.width = width
    },
    onResize () {
      this.calcHeight()
      this.calcWidth()
    },
    setItemVariant (item) {
      return item?.deleted_at ? 'bg-danger' : ''
    }
  }
}
</script>

<style scoped>

</style>
